import React, { useState } from 'react';
import * as S from './AdvancesStyles';
import { toast } from 'react-toastify';

const fmt = n => (n < 0 ? '−₹' : '₹') + Math.round(Math.abs(n)).toLocaleString('en-IN');
const sum = (arr, f) => arr.reduce((s, x) => s + f(x), 0);

const PayrollTab = ({
  txns,
  members,
  payrollBatches,
  isAdmin = true,
  onMarkPayrollPaid,
  onOpenPassbook
}) => {
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);

  // Compute payable reimbursements for all staff members with unpaid expenses
  const payableData = members
    .map(m => {
      const rows = txns.filter(
        t =>
          t.type === 'expense' &&
          (String(t.memberId) === String(m.id) || (t.memberName && t.memberName.toLowerCase() === m.name.toLowerCase())) &&
          t.fundingSource === 'reimbursement' &&
          t.approvalStatus === 'approved' &&
          t.payoutStatus !== 'paid'
      );
      const total = sum(rows, t => t.amount);
      return { m, rows, total };
    })
    .filter(item => item.total > 0)
    .sort((a, b) => b.total - a.total);

  const allSelected =
    payableData.length > 0 &&
    payableData.every(item => selectedMemberIds.includes(item.m.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedMemberIds([]);
    } else {
      setSelectedMemberIds(payableData.map(item => item.m.id));
    }
  };

  const toggleSelectMember = id => {
    if (selectedMemberIds.includes(id)) {
      setSelectedMemberIds(selectedMemberIds.filter(mId => mId !== id));
    } else {
      setSelectedMemberIds([...selectedMemberIds, id]);
    }
  };

  const selectedTotal = sum(
    payableData.filter(item => selectedMemberIds.includes(item.m.id)),
    item => item.total
  );

  const handleExportCsv = () => {
    const targets = selectedMemberIds.length
      ? payableData.filter(item => selectedMemberIds.includes(item.m.id))
      : payableData;

    if (!targets.length) {
      toast.error('No reimbursement records to export.');
      return;
    }

    let csv = 'Member Name,Role,Expense Count,Total Payable (INR)\n';
    targets.forEach(item => {
      csv += `"${item.m.name}","${item.m.role || 'Staff'}",${item.rows.length},${item.total}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `reimbursement_payroll_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported CSV for ${targets.length} members!`);
  };

  const handleMarkPaid = () => {
    if (!selectedMemberIds.length) {
      toast.error('Select at least one member first.');
      return;
    }
    onMarkPayrollPaid(selectedMemberIds);
    setSelectedMemberIds([]);
  };

  return (
    <div>
      <S.CardBox style={{ marginBottom: '16px' }}>
        <h3>Unpaid reimbursement payable</h3>
        <div className="hint">
          Select members to include in this payout batch. Settle payables individually or export CSV for bulk processing.
        </div>
        <S.TableWrap style={{ marginTop: '10px' }}>
          <table>
            <thead>
              <tr>
                {isAdmin && (
                  <th style={{ width: '30px' }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th>Member</th>
                <th># Expenses</th>
                <th>Amount payable</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payableData.length ? (
                payableData.map(item => (
                  <tr key={item.m.id}>
                    {isAdmin && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedMemberIds.includes(item.m.id)}
                          onChange={() => toggleSelectMember(item.m.id)}
                        />
                      </td>
                    )}
                    <td>
                      <span
                        className="member-link"
                        onClick={() => onOpenPassbook(item.m.id)}
                      >
                        {item.m.name}
                      </span>
                    </td>
                    <td>{item.rows.length}</td>
                    <td>
                      <strong>{fmt(item.total)}</strong>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {isAdmin && (
                          <S.Btn ghost sm onClick={() => onMarkPayrollPaid([item.m.id])}>
                            Mark Paid
                          </S.Btn>
                        )}
                        <S.Btn ghost sm onClick={() => onOpenPassbook(item.m.id)}>
                          View
                        </S.Btn>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 5 : 4} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Everyone's been paid out.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </S.TableWrap>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px' }}>
          <div>
            {isAdmin && (
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                Selected total:{' '}
                <span style={{ fontSize: '15px', fontWeight: 800, color: '#111827' }}>
                  {fmt(selectedTotal)}
                </span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <S.Btn ghost onClick={handleExportCsv}>
              Export CSV
            </S.Btn>
            {isAdmin && (
              <S.Btn
                accent
                onClick={handleMarkPaid}
                disabled={!selectedMemberIds.length}
                style={{ opacity: selectedMemberIds.length ? 1 : 0.5 }}
              >
                Mark selected as Paid
              </S.Btn>
            )}
          </div>
        </div>
      </S.CardBox>

      <S.CardBox>
        <h3>Batch history</h3>
        <S.TableWrap style={{ marginTop: '10px' }}>
          <table>
            <thead>
              <tr>
                <th>Batch</th>
                <th>Period</th>
                <th>Members</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payrollBatches.length ? (
                payrollBatches.map(b => (
                  <tr key={b.id}>
                    <td>
                      <strong>{b.id}</strong>
                    </td>
                    <td>{b.period}</td>
                    <td>
                      {b.memberIds
                        .map(id => members.find(m => m.id === id)?.name)
                        .filter(Boolean)
                        .join(', ')}
                    </td>
                    <td>{fmt(b.total)}</td>
                    <td>
                      <S.Pill variant={b.status === 'closed' ? 'paid' : 'pending'}>
                        {b.status}
                      </S.Pill>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    No payroll batches recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </S.TableWrap>
      </S.CardBox>
    </div>
  );
};

export default PayrollTab;
